import { useRef, useState } from "react";
import { FileText, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import type { Workshop } from "@/content/workshops";
import { PHASES_BY_WORKSHOP } from "@/features/run-sequence/run-sequence-content";
import {
  defaultObjectiveToRunSeq,
  resolvePhaseObjectives,
  SITE_DESTINATIONS,
  type ObjectiveLink,
  type RunSeqObjective,
} from "../workshop-override-content";
import {
  clearEffectiveOverrideField,
  setEffectiveOverride,
  useEffectiveWorkshopOverride,
} from "@/stores/useEffectiveWorkshopOverride";
import { uploadWorkshopFile } from "@/stores/useWorkshopOverrides";
import { DocsApiError } from "@/lib/docs-api";
import {
  AddButton,
  Field,
  IconBtn,
  ListCard,
  SectionFrame,
  Select,
  TextInput,
} from "../editor-kit";
import { moveItem, sameJson } from "../editor-utils";

type Draft = Record<string, RunSeqObjective[]>;
type LinkAction = "none" | "page" | "website" | "file";

function linkToAction(link?: ObjectiveLink): LinkAction {
  if (!link) return "none";
  if (link.kind === "url") return "website";
  if (link.kind === "file") return "file";
  return "page"; // modal | route
}

/** The SITE_DESTINATIONS key that matches a page-style link. */
function destKeyOf(link?: ObjectiveLink): string {
  const first = SITE_DESTINATIONS[0]!.key;
  if (!link) return first;
  const d = SITE_DESTINATIONS.find(
    (dst) =>
      (dst.modal && link.kind === "modal" && dst.modal === link.modal) ||
      (dst.route && link.kind === "route" && dst.route === link.route),
  );
  return d?.key ?? first;
}

function destToLink(key: string): ObjectiveLink {
  const d = SITE_DESTINATIONS.find((dst) => dst.key === key) ?? SITE_DESTINATIONS[0]!;
  return d.modal ? { kind: "modal", modal: d.modal } : { kind: "route", route: d.route! };
}

function defaultsFor(wsId: Workshop["id"], phaseId: string): RunSeqObjective[] {
  const phase = (PHASES_BY_WORKSHOP[wsId] ?? []).find((p) => p.id === phaseId);
  return (phase?.objectives ?? []).map(defaultObjectiveToRunSeq);
}

/** Run-sequence objective editor — per phase, edit the objective list and
 *  choose where each objective links (a page on the site / a website / a
 *  file). */
export function RunSequenceEditor({ wsId }: { wsId: Workshop["id"] }) {
  const override = useEffectiveWorkshopOverride(wsId);
  const phases = PHASES_BY_WORKSHOP[wsId] ?? [];

  const current: Draft = {};
  for (const p of phases) {
    current[p.id] = resolvePhaseObjectives(override, p.id, p.objectives ?? []);
  }
  const [draft, setDraft] = useState<Draft>(current);

  const fileRef = useRef<HTMLInputElement>(null);
  const pendingRef = useRef<{ phaseId: string; index: number } | null>(null);
  const [busy, setBusy] = useState(false);

  const setObjs = (phaseId: string, objs: RunSeqObjective[]) =>
    setDraft((d) => ({ ...d, [phaseId]: objs }));
  const setObj = (phaseId: string, i: number, patch: Partial<RunSeqObjective>) =>
    setDraft((d) => ({
      ...d,
      [phaseId]: (d[phaseId] ?? []).map((o, j) => (j === i ? { ...o, ...patch } : o)),
    }));

  const onAction = (phaseId: string, i: number, action: LinkAction, link?: ObjectiveLink) => {
    if (action === "none") setObj(phaseId, i, { link: undefined });
    else if (action === "page") setObj(phaseId, i, { link: destToLink(destKeyOf(link)) });
    else if (action === "website")
      setObj(phaseId, i, {
        link: { kind: "url", url: link?.kind === "url" ? link.url : "" },
      });
    else {
      // "file" — prompt immediately; the link is only set once a file lands.
      pendingRef.current = { phaseId, index: i };
      fileRef.current?.click();
    }
  };

  const onFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const target = pendingRef.current;
    pendingRef.current = null;
    if (!file || !target) return;
    setBusy(true);
    try {
      const upload = await uploadWorkshopFile(wsId, "objectives", file);
      if (!upload) {
        toast.error("File links need the document backend (BFF).");
        return;
      }
      setObj(target.phaseId, target.index, { link: { kind: "file", upload } });
      toast.success(`Attached ${file.name}`);
    } catch (err) {
      toast.error(err instanceof DocsApiError ? err.message : "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    const runSequence: Draft = {};
    for (const p of phases) {
      const objs = draft[p.id] ?? [];
      if (!sameJson(objs, defaultsFor(wsId, p.id))) runSequence[p.id] = objs;
    }
    setEffectiveOverride(wsId, {
      runSequence: Object.keys(runSequence).length > 0 ? runSequence : undefined,
    });
  };

  return (
    <SectionFrame
      description="Edit each phase's objectives and choose where each one links — a page on the site, a website, or a file."
      overridden={override?.runSequence !== undefined}
      dirty={!sameJson(draft, current)}
      onSave={save}
      onReset={() => {
        clearEffectiveOverrideField(wsId, "runSequence");
        const reseed: Draft = {};
        for (const p of phases) reseed[p.id] = defaultsFor(wsId, p.id);
        setDraft(reseed);
      }}
    >
      {phases.length === 0 && (
        <p className="font-mono text-fs-label text-[color:var(--text-muted)]">
          This workshop has no run-sequence phases.
        </p>
      )}

      {phases.map((p) => {
        const objs = draft[p.id] ?? [];
        return (
          <ListCard key={p.id} title={p.title}>
            {objs.map((o, i) => {
              const action = linkToAction(o.link);
              return (
                <div
                  key={i}
                  className="flex flex-col gap-2 rounded border border-[rgba(var(--accent-true-blue-rgb),0.12)] p-2"
                >
                  <div className="flex items-start gap-2">
                    <TextInput
                      value={o.text}
                      placeholder="Objective"
                      onChange={(e) => setObj(p.id, i, { text: e.target.value })}
                      className="flex-1"
                    />
                    <IconBtn
                      label="Move up"
                      onClick={() => setObjs(p.id, moveItem(objs, i, -1))}
                    >
                      ↑
                    </IconBtn>
                    <IconBtn
                      label="Move down"
                      onClick={() => setObjs(p.id, moveItem(objs, i, 1))}
                    >
                      ↓
                    </IconBtn>
                    <IconBtn
                      label="Remove objective"
                      danger
                      onClick={() => setObjs(p.id, objs.filter((_, j) => j !== i))}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </IconBtn>
                  </div>

                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <Field label="Links to">
                      <Select
                        value={action}
                        onChange={(v) => onAction(p.id, i, v as LinkAction, o.link)}
                        options={[
                          { value: "none", label: "No link" },
                          { value: "page", label: "A page on the site" },
                          { value: "website", label: "A website" },
                          { value: "file", label: "A file" },
                        ]}
                      />
                    </Field>

                    {action === "page" && (
                      <Field label="Destination">
                        <Select
                          value={destKeyOf(o.link)}
                          onChange={(v) => setObj(p.id, i, { link: destToLink(v) })}
                          options={SITE_DESTINATIONS.map((d) => ({
                            value: d.key,
                            label: d.label,
                          }))}
                        />
                      </Field>
                    )}

                    {action === "website" && (
                      <Field label="Website URL">
                        <TextInput
                          value={o.link?.kind === "url" ? o.link.url : ""}
                          placeholder="https://..."
                          onChange={(e) => setObj(p.id, i, { link: { kind: "url", url: e.target.value } })}
                        />
                      </Field>
                    )}

                    {action === "file" && (
                      <Field label="File">
                        <div className="flex items-center gap-2">
                          <FileText
                            className="h-3.5 w-3.5 flex-shrink-0 text-[color:var(--accent-true-blue)]"
                            aria-hidden
                          />
                          <span className="min-w-0 flex-1 truncate font-mono text-fs-label text-[color:var(--text-secondary)]">
                            {o.link?.kind === "file" ? o.link.upload.filename : "No file"}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              pendingRef.current = { phaseId: p.id, index: i };
                              fileRef.current?.click();
                            }}
                            disabled={busy}
                            className="rounded border border-[rgba(var(--accent-true-blue-rgb),0.3)] px-2 py-1 font-mono text-fs-label tracking-widest text-[color:var(--text-secondary)] uppercase transition-colors hover:text-[color:var(--text-primary)] disabled:opacity-40"
                          >
                            {o.link?.kind === "file" ? "Replace" : "Upload"}
                          </button>
                        </div>
                      </Field>
                    )}
                  </div>
                </div>
              );
            })}
            <AddButton
              label="Add objective"
              onClick={() => setObjs(p.id, [...objs, { text: "" }])}
            />
          </ListCard>
        );
      })}

      {busy && (
        <div className="flex items-center gap-2 font-mono text-fs-label text-[color:var(--text-muted)]">
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Uploading…
        </div>
      )}
      <input ref={fileRef} type="file" className="hidden" onChange={(e) => void onFileChosen(e)} />
    </SectionFrame>
  );
}
